import { useGetZenDeskFeatureFlagQuery } from "common/types/mainHooks";
import { useEffect, useRef } from "react";
import { addError } from "@jsq/observability";
import { type ZendeskWidgetConstructor } from "./types";

declare global {
  const zE: ZendeskWidgetConstructor | undefined;
  // Defined server-side in widgets.jinja2 only when the new navigation header is
  // active, where the widget is hidden by default and surfaced via the header.
  const toggleSupportWidget: (() => void) | undefined;
}

async function getZendeskJWT() {
  try {
    const response = await fetch("/zendesk_messaging_login", {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return (await response.json()) as string;
  } catch (error) {
    addError(`Failed to retrieve JWT token for Zendesk: ${error}`);
    return null;
  }
}

export function useZendeskMessagingWidget() {
  const { data, refetch } = useGetZenDeskFeatureFlagQuery();
  const hasLoaded = useRef(false);

  if (data?.devFeatures.lpZendeskMessagingWidget && !hasLoaded.current) {
    const script = document.createElement("script");
    script.src =
      "https://static.zdassets.com/ekr/snippet.js?key=26048bac-5cf5-40e1-aef3-ab47a750b0fd";
    script.id = "ze-snippet";
    script.async = true;

    script.onload = () => {
      getZendeskJWT().then((token) => {
        if (token && zE) {
          zE(
            "messenger",
            "loginUser",
            function jwtCallback(callback) {
              callback(token);
            },
            function loginCallback(error) {
              if (error) {
                // @ts-expect-error this is fine
                const { type, reason, message } = error;
                addError(`Zendesk init error: ${type} - ${reason} - ${message}`);
              }
            },
          );
        } else {
          addError("Failed to retrieve JWT token for Zendesk");
        }
      });
    };

    document.head.appendChild(script);
    hasLoaded.current = true;
  }

  return { refetch };
}

// Hide the floating Zendesk launcher. Safe no-op when the widget snippet has not
// loaded or the widget is disabled for the current user/arena.
export function hideZendeskMessagingWidget() {
  if (typeof zE !== "undefined" && zE) {
    zE("messenger", "hide");
  }
}

// Restore the launcher. Under the new navigation header the widget is hidden by
// default and surfaced through the header (toggleSupportWidget is defined), so we
// must not force it visible there — only re-show it for legacy navigation.
export function restoreZendeskMessagingWidget() {
  if (typeof toggleSupportWidget !== "undefined") {
    return;
  }
  if (typeof zE !== "undefined" && zE) {
    zE("messenger", "show");
  }
}

// Hides the launcher while the calling component is mounted and restores it on
// unmount. Useful for fullscreen takeover flows where the launcher overlaps
// bottom-anchored controls such as the Back button.
export function useHideZendeskMessagingWidget() {
  useEffect(() => {
    hideZendeskMessagingWidget();
    return () => {
      restoreZendeskMessagingWidget();
    };
  }, []);
}
